<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import OrganizationUserAutocomplete from '@/components/input/OrganizationUserAutocomplete.vue'
import { IssueSearchQuery } from '@/generated/graphql'
import Checkbox from '@/components/input/Checkbox.vue'
import TextField from '@/components/input/TextField.vue'
import { ref, watch, computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'

const model = defineModel<IssueSearchQuery>({ required: true })
const props = defineProps<{ labelIds?: string[] }>()
const emit = defineEmits<{ 'update:labelIds': [value: string[] | undefined] }>()

const poNumbers = ref<string>()
watch(poNumbers, (v) => {
  if (!v) {
    model.value.poNumbers = undefined
    return
  }

  model.value.poNumbers = v.split(',').map((n) => n.trim())
})

const serialNumbers = ref<string>()
watch(serialNumbers, (v) => {
  if (!v) {
    model.value.serialNumbers = undefined
    return
  }

  model.value.serialNumbers = v.split(',').map((n) => n.trim())
})

const { t } = useI18n()

const labelsQuery = useQuery(gql`
  query IssueFiltersLabels {
    issue {
      orgIssueLabels {
        id
        name
        color
      }
    }
  }
`)
type LocalLabel = { id: string; name: string; color: string }
const labels = computed<LocalLabel[]>(() => labelsQuery.result.value?.issue.orgIssueLabels || [])
</script>

<template>
  <v-card>
    <v-card-text>
      <organization-user-autocomplete
        v-model="model.assignedToUserId"
        :label="t('entity.issue.filter.assignedToUser')"
      />
      <checkbox
        v-model="model.isResolved"
        :label="t('entity.issue.filter.isResolved')"
        indeterminate
      />
      <text-field v-model="poNumbers" :label="t('entity.issue.filter.poNumbers')" />
      <text-field v-model="serialNumbers" :label="t('entity.issue.filter.serialNumbers')" />
      <v-select
        :model-value="props.labelIds"
        :items="labels"
        :label="t('entity.issueLabel.plural')"
        item-title="name"
        item-value="id"
        multiple
        chips
        closable-chips
        @update:model-value="
          (v) => {
            const arr = v as string[] | undefined
            emit('update:labelIds', arr && arr.length > 0 ? arr : undefined)
          }
        "
      >
        <template #chip="{ item, props: chipProps }">
          <v-chip v-bind="chipProps" size="small">
            <span
              :style="{
                background: item.raw.color,
                width: '10px',
                height: '10px',
                display: 'inline-block',
                borderRadius: '2px',
                marginRight: '6px',
                border: '1px solid #ccc',
              }"
            />
            {{ item.raw.name }}
          </v-chip>
        </template>
        <template #item="{ props: itemProps, item }">
          <v-list-item v-bind="itemProps">
            <template #prepend>
              <div
                :style="{
                  background: item.raw.color,
                  width: '14px',
                  height: '14px',
                  borderRadius: '3px',
                  border: '1px solid #ccc',
                  marginRight: '8px',
                }"
              />
            </template>
          </v-list-item>
        </template>
      </v-select>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss"></style>
